import { useState } from 'react'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import { api } from '../../services/api'

interface SeedResult {
  transactionsCreated: number
  posTransactionsCreated: number
  posBatchesCreated: number
  deliveryOrdersCreated: number
  deliveryPayoutsCreated: number
  accountsCreated: number
  categoriesCreated: number
  employeesCreated: number
}

export default function SeedData() {
  const [loading, setLoading] = useState<string | null>(null)
  const [result, setResult] = useState<SeedResult | null>(null)

  const handleSeedRestaurant = async () => {
    setLoading('restaurant')
    try {
      const response = await api.seedRestaurant('demo-location', 30)
      if (response.success && response.data) {
        setResult(response.data as SeedResult)
      }
    } catch (err) {
      console.error(err)
      // Demo mode - simulate result
      setResult({
        transactionsCreated: 87,
        posTransactionsCreated: 2340,
        posBatchesCreated: 30,
        deliveryOrdersCreated: 156,
        deliveryPayoutsCreated: 10,
        accountsCreated: 24,
        categoriesCreated: 6,
        employeesCreated: 5,
      })
    } finally {
      setLoading(null)
    }
  }

  const handleSeedRetail = async () => {
    setLoading('retail')
    try {
      const response = await api.seedRetail('demo-location', 30)
      if (response.success && response.data) {
        setResult(response.data as SeedResult)
      }
    } catch (err) {
      // Demo mode - simulate result
      setResult({
        transactionsCreated: 65,
        posTransactionsCreated: 1560,
        posBatchesCreated: 26,
        deliveryOrdersCreated: 0,
        deliveryPayoutsCreated: 0,
        accountsCreated: 12,
        categoriesCreated: 4,
        employeesCreated: 0,
      })
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Demo Data Generator</h1>
        <p className="text-gray-500 mt-1">Seed your account with realistic demo data for testing</p>
      </div>

      {/* Seed Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        <Card>
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🍽️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Restaurant Demo</h2>
            <p className="text-gray-500 mb-6">
              Generate 30 days of restaurant data including POS sales, delivery orders, 
              food vendor payments, and tip records.
            </p>
            <div className="text-left text-sm text-gray-600 mb-6 space-y-2">
              <p className="font-medium text-gray-700 mb-2">Integrations simulated:</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center px-2 py-1 rounded bg-white border text-xs font-medium">
                  <span className="w-2 h-2 bg-black rounded mr-1.5"></span>
                  Square POS
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded bg-red-50 text-red-700 text-xs font-medium">
                  DoorDash
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded bg-green-50 text-green-700 text-xs font-medium">
                  Uber Eats
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded bg-orange-50 text-orange-700 text-xs font-medium">
                  Grubhub
                </span>
              </div>
              <ul className="space-y-1">
                <li>✓ Daily Square POS batches with realistic fees</li>
                <li>✓ Delivery orders with platform commissions</li>
                <li>✓ Sysco, US Foods vendor payments</li>
                <li>✓ Employee tip records</li>
              </ul>
            </div>
            <Button
              onClick={handleSeedRestaurant}
              loading={loading === 'restaurant'}
              className="w-full"
            >
              Generate Restaurant Data
            </Button>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🏪</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Retail Demo</h2>
            <p className="text-gray-500 mb-6">
              Generate 30 days of retail store data including POS sales, 
              wholesale purchases, and standard operating expenses.
            </p>
            <div className="text-left text-sm text-gray-600 mb-6 space-y-2">
              <p className="font-medium text-gray-700 mb-2">Integrations simulated:</p>
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center px-2 py-1 rounded bg-white border text-xs font-medium">
                  <span className="w-2 h-2 bg-black rounded mr-1.5"></span>
                  Square POS
                </span>
              </div>
              <ul className="space-y-1">
                <li>✓ Daily Square POS batches</li>
                <li>✓ Wholesale vendor payments</li>
                <li>✓ Inventory-related transactions</li>
                <li>✓ Standard operating expenses</li>
              </ul>
            </div>
            <Button
              onClick={handleSeedRetail}
              loading={loading === 'retail'}
              variant="secondary"
              className="w-full"
            >
              Generate Retail Data
            </Button>
          </div>
        </Card>
      </div>

      {/* Results */}
      {result && (
        <Card className="max-w-4xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-green-500 mr-2">✓</span>
            Data Generated Successfully
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {result.transactionsCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.transactionsCreated}</p>
                <p className="text-sm text-gray-500">Bank Transactions</p>
              </div>
            )}
            {result.posTransactionsCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.posTransactionsCreated}</p>
                <p className="text-sm text-gray-500">POS Transactions</p>
              </div>
            )}
            {result.posBatchesCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.posBatchesCreated}</p>
                <p className="text-sm text-gray-500">POS Batches</p>
              </div>
            )}
            {result.deliveryOrdersCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.deliveryOrdersCreated}</p>
                <p className="text-sm text-gray-500">Delivery Orders</p>
              </div>
            )}
            {result.accountsCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.accountsCreated}</p>
                <p className="text-sm text-gray-500">Accounts Created</p>
              </div>
            )}
            {result.categoriesCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.categoriesCreated}</p>
                <p className="text-sm text-gray-500">Categories</p>
              </div>
            )}
            {result.employeesCreated > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.employeesCreated}</p>
                <p className="text-sm text-gray-500">Employees</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Info */}
      <Card className="max-w-4xl bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <span className="text-xl">💡</span>
          <div>
            <h4 className="font-medium text-gray-900">Demo Mode</h4>
            <p className="text-sm text-gray-600 mt-1">
              This generates realistic mock data for demonstration purposes. 
              In production, this would be replaced with actual integrations to 
              Plaid, Square, DoorDash, and other services.
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}
